import { useCallback, useContext, useMemo } from 'react'

import { SanityFrameBuilderProfile } from '@frame-builder/data/sanity/queries/types/profile'
import { type SanityFrameBuilderTexture } from '@frame-builder/data/sanity/queries/types/texture'
import { FrameBuilderSettingsContext } from '@frame-builder/contexts/settings-context'
import { FrameBuilderSelectedStateContext } from '@frame-builder/contexts/state-context'
import { FrameBuilderStringsContext } from '@frame-builder/contexts/strings-context'
import { FrameBuilderValidationContext } from '@frame-builder/contexts/validation-context'
import { getSanityImageUrl } from '@frame-builder/lib/image'

import Tooltip from '@components/tooltip'
import ListContainer from '@frame-builder/components/utility/list-container'
import SmallGraphic from '@frame-builder/components/utility/small-graphic'

interface ProfileDetailsProps {
  profile: SanityFrameBuilderProfile
}

const ProfileDetails = ({ profile }: ProfileDetailsProps) => {
  const { selectedState, dispatchSelectedState } = useContext(
    FrameBuilderSelectedStateContext,
  )
  const { lastValidState } = useContext(FrameBuilderValidationContext)
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)
  const { frameBuilderConfiguration } = useContext(FrameBuilderSettingsContext)

  const filteredTextures = useMemo(() => {
    return frameBuilderConfiguration.textures.filter((texture) => {
      return frameBuilderConfiguration.profileTextures.some(
        (profileTexture) => {
          return (
            profileTexture.profile._ref === profile._id &&
            profileTexture.texture._ref === texture._id
          )
        },
      )
    })
  }, [frameBuilderConfiguration, profile])

  const selectedProfileTexture = useMemo(() => {
    return frameBuilderConfiguration.profileTextures.find((profileTexture) => {
      return (
        profileTexture.profile._ref === profile._id &&
        profileTexture.texture._ref === selectedState.texture?._id
      )
    })
  }, [frameBuilderConfiguration, profile, selectedState.texture])

  const selectTexture = useCallback(
    (texture: SanityFrameBuilderTexture) => {
      dispatchSelectedState({
        type: 'frameStyle',
        texture,
      })
    },
    [dispatchSelectedState],
  )

  return (
    <div className="w-full pt-2.5 pb-4 px-2.5 space-y-2.5">
      <p className="flex gap-2.5">
        <span>{frameBuilderStrings.frameStyleTextureLabel}</span>
        {!!selectedState.texture && (
          <span className="text-gray-700">
            {[selectedState.texture.title, selectedProfileTexture?.sku]
              .filter(Boolean)
              .join(', ')}
          </span>
        )}
      </p>

      <ListContainer gapSize="xs">
        {filteredTextures.map((texture) => {
          const profileTexture = frameBuilderConfiguration.profileTextures.find(
            (profileTexture) => {
              return (
                profileTexture.profile._ref === profile._id &&
                profileTexture.texture._ref === texture._id
              )
            },
          )
          const image = texture.icon ?? texture.image
          const imageUrl = getSanityImageUrl(image, {
            width: 32,
            height: 32,
          })

          return (
            <Tooltip
              key={texture._id}
              content={
                <span>
                  {[texture.title, profileTexture?.sku]
                    .filter(Boolean)
                    .join(', ')}
                </span>
              }
              placement="top"
              trigger={['click', 'hover']}
              className="inline-block ml-1"
              tooltipClassName="p-2.5 rounded text-xs text-white bg-black"
            >
              <SmallGraphic
                key={texture._id}
                backgroundColor="white"
                backgroundImage={imageUrl}
                selected={selectedState.texture?._id === texture._id}
                error={lastValidState.texture?._id === texture._id}
                onClick={() => selectTexture(texture)}
              />
            </Tooltip>
          )
        })}
      </ListContainer>
    </div>
  )
}

export default ProfileDetails
